<template>
  <div class="sherlock-app--loading" v-if="$route.meta.logged && !currentUser">
    <WSpinner size="xlarge" />
  </div>

  <template v-else>
    <WSidebar
      v-if="route.meta.logged"
      :label="'Sherlock'"
      :user="sidebarUser"
      :logo="logoUrl"
      logo-background-size="contain"
      @w-logout="logout"
    >
      <div slot="content" class="sidebar-menu">
        <template
          v-for="(item, menuIndex) in menu"
          :key="`${
            item.path ?? item.link ?? item.title ?? 'item'
          }-${menuIndex}`"
        >
          <WAccordion
            v-if="item.children?.length"
            :label="item.title"
            label-size="medium"
            class="sidebar-menu__accordion"
          >
            <ul slot="content">
              <li
                v-for="(child, childIndex) in item.children"
                :key="`${
                  child.path ?? child.link ?? child.title ?? 'child'
                }-${childIndex}`"
                hoverable
                cursor="pointer"
              >
                <router-link
                  :to="child.link ?? ''"
                  :class="{
                    'menu-item-link': true,
                    'menu-item-link--nested': true,
                    'menu-item-link--padding': true,
                    'menu-item-link--active': route.path.includes(
                      child.path ?? ''
                    )
                  }"
                >
                  <WIcon :icon="child.icon" size="medium" />
                  <WLabel
                    :mode="
                      route.path.includes(child.path ?? '')
                        ? 'active'
                        : 'primary'
                    "
                    >{{ child.title }}</WLabel
                  >
                </router-link>
              </li>
            </ul>
          </WAccordion>
          <WListItem v-else hoverable cursor="pointer">
            <router-link
              :to="item.link ?? ''"
              :class="{
                'menu-item-link': true,
                'menu-item-link--active': route.path.includes(item.path ?? '')
              }"
            >
              <WIcon :icon="item.icon" />
              <WLabel
                :mode="
                  route.path.includes(item.path ?? '') ? 'active' : 'primary'
                "
                >{{ item.title }}</WLabel
              >
            </router-link>
          </WListItem>
        </template>
      </div>
    </WSidebar>

    <main
      class="sherlock-app"
      :class="{ 'sherlock-app--logged': route.meta.logged }"
    >
      <RouterView v-slot="{ Component }" :key="route.fullPath">
        <component :is="Component" />
      </RouterView>
    </main>
  </template>
  <WSnackbar ref="snackbarRef" />
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import HTTP_ERRORS from '@/constants/errors'
import { criticalError } from '@/plugins/api'
import { auth } from '@/plugins/firebase'
import {
  WAccordion,
  WIcon,
  WLabel,
  WListItem,
  WSidebar,
  WSnackbar,
  WSpinner
} from '@alice-health/wonderland-vue'
import { getUser } from '@/api'
import type { User } from '@/models/user'
import logoUrl from '@/assets/logo.png'
import APP_MENU, { type MenuRule } from '@/constants/menu'

const route = useRoute()
const router = useRouter()
const store = useStore()

type CurrentUser = User & { photoURL?: string }
const currentUser = ref<CurrentUser | null>(null)
interface SnackbarElement {
  add?: (props: { message: string; icon?: string }) => void
}
const snackbarRef = ref<{ $el?: SnackbarElement } | null>(null)

const sidebarUser = computed(() => {
  const user = currentUser.value
  return {
    name: user?.name ?? '',
    image: user?.photoURL ?? ''
  }
})

function menuItemShouldBeVisible(item: {
  link?: string
  path?: string
  children?: unknown[]
}): boolean {
  return !!(item.link ?? (item.children && item.children.length > 0))
}

function shouldShowMenu(rules: MenuRule[]): boolean {
  return rules.some((rule: MenuRule) =>
    store.getters['featureConfig/isVisibleByUserIdRole']({
      name: rule.featureName,
      userIdRole: currentUser.value?.[rule.field as keyof CurrentUser] ?? ''
    })
  )
}

const menu = computed(() => {
  return APP_MENU.map(item => {
    const isActive = item.path && route.path.includes(item.path)
    const children = item.children
      ?.map(child => ({
        ...child,
        active: route.path.includes(child.path ?? '')
      }))
      .filter(child => menuItemShouldBeVisible(child))
    const hasActiveChildren = children?.some(child => child.active) ?? false

    return {
      ...item,
      active: !!(isActive || hasActiveChildren),
      children
    }
  })
    .filter(item => menuItemShouldBeVisible(item))
    .filter(item => !item.rules || shouldShowMenu(item.rules))
})

function notify({
  message,
  details,
  type = 'error'
}: {
  code?: number
  message: string
  details?: string
  type?: 'success' | 'error'
  timeout?: number
}): void {
  const fullMessage = details ? `${message} ${details}` : message
  const icon = type === 'success' ? 'icCheckFilled' : 'icAlertTriangle'
  snackbarRef.value?.$el?.add?.({ message: fullMessage, icon })
}

async function logout(): Promise<void> {
  await auth.signOut()
  router.push('/login')
}

onBeforeMount(() => {
  auth.onAuthStateChanged(async user => {
    if (!user) {
      currentUser.value = null
      return
    }

    currentUser.value = {
      photoURL: user.photoURL ?? undefined
    } as CurrentUser

    store.dispatch('featureConfig/getFeatureConfigs')

    const userFull = await getUser()
    currentUser.value = {
      ...currentUser.value,
      ...userFull
    } as CurrentUser
  })

  criticalError.then((error: unknown) => {
    const err = error as { status: number; url: string }
    notify({
      message: HTTP_ERRORS[err.status as keyof typeof HTTP_ERRORS],
      details: err.url.split('/').pop()
    })
  })
})

provide('notify', notify)
</script>

<style lang="scss">
@import '@alice-health/design-tokens/dist/css/reset';
@import '@alice-health/design-tokens/dist/css/global-font-family';
@import '@alice-health/design-tokens/dist/css/index';

#app {
  margin: 0;
  display: flex;
}

.sherlock-app {
  width: 100%;

  &--logged {
    padding: var(--gl-spacing-06);
    width: calc(100% - 216px);
  }

  &--loading {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    width: 100vw;
  }
}

.list-style-none {
  list-style-type: none;
  padding: 0 !important;
}

.avatar-size {
  width: 40px;
  height: 40px;
}

.menu-item-link {
  display: flex;
  align-items: center;
  gap: var(--gl-spacing-02);
  text-decoration: none;
  color: var(--sys-color-content-primary);
  width: 100%;

  &--padding {
    padding: var(--gl-spacing-04) var(--gl-spacing-02);
  }

  &--nested {
    padding-left: var(--gl-spacing-04);
  }

  &--active {
    color: var(--sys-color-content-active);
    font-weight: 600;
  }
}
</style>
